// TRACE SEO content pages — data model + registry.
//
// Each page is prerendered to its own static HTML with its own <head>
// (title / description / canonical / OG / hreflang / Article + BreadcrumbList
// JSON-LD) so it is fully crawlable, then hydrated client-side. The same array
// drives the client routes (router.tsx) and the build-time prerender
// (scripts/entry-ssr.tsx + prerender.mjs). Single source of truth.

export interface SeoSection {
  h2: string;
  paragraphs: string[];
  bullets?: string[];
}
export interface SeoFaq {
  q: string;
  a: string;
}
export interface SeoLink {
  label: string;
  href: string;
}
export interface SeoAlternate {
  hreflang: string; // "en" | "it" | "x-default"
  href: string; // absolute path, e.g. "/technical-rider"
}

export interface SeoPageData {
  path: string; // "/technical-rider", "/for/festival-organizers", "/it/rider-tecnico"
  lang: "en" | "it";
  title: string;
  metaDescription: string;
  ogTitle: string;
  ogDescription: string;
  breadcrumb: string;
  h1: string;
  intro: string;
  sections: SeoSection[];
  faq: SeoFaq[];
  related: SeoLink[];
  alternates?: SeoAlternate[];
}

export const SEO_PAGES: SeoPageData[] = [
  {
    "path": "/technical-rider",
    "lang": "en",
    "title": "Technical Rider: What It Is and How to Read One",
    "metaDescription": "A technical rider lists what an artist needs on stage: backline, inputs, monitoring, power, hospitality. Learn what's inside one and how to read it.",
    "ogTitle": "What Is a Technical Rider, and How Do You Read One?",
    "ogDescription": "The technical rider is the document that tells festival and production teams what an artist needs on stage. Here's what's inside, how DJ, band, and live-electronic riders differ, and how to read a stack of them.",
    "breadcrumb": "Technical rider",
    "h1": "What Is a Technical Rider?",
    "intro": "A technical rider is the document an artist or their tour sends ahead of a show that spells out exactly what they need on stage to perform: the instruments and backline, the microphones and channels, the monitoring, the power, and often the hospitality that goes with it. If you run a festival or a production company, the technical rider is what lands in your inbox, one per act, and it is the raw material for every equipment order, stage changeover, and crew rota you build.",
    "sections": [
      {
        "h2": "What a technical rider includes",
        "paragraphs": [
          "The exact contents vary by artist and genre, but most technical riders share a recognizable spine. Learning to spot each part is the first step to reading one quickly and knowing what it will cost you.",
          "Once you can name the sections at a glance, a dense PDF stops being a wall of text and becomes a checklist you can work through."
        ],
        "bullets": [
          "Stage plot: a top-down diagram showing where each performer, instrument, amplifier, and monitor sits. It tells you the physical layout, how much space you need, and how many positions to prepare.",
          "Input list (channel list): a numbered table of every signal going into the console (kick, snare, bass DI, vocal mic, keys, and so on) with the preferred mic or DI for each. This drives your console and stagebox channel counts.",
          "Backline: the instruments and amplifiers the artist expects you to provide, from drum kit and guitar amps to keyboards and DJ gear. This is where most equipment cost and most conflicts live.",
          "Monitoring: how performers hear themselves, whether through wedges, in-ear monitors (IEMs), side-fills, and how many separate mixes.",
          "Power and staging: clean power, phases and distribution, riser sizes, and any structural requirements.",
          "Hospitality and logistics: dressing rooms, catering, ground transport, and accommodation, sometimes split into a separate rider."
        ]
      },
      {
        "h2": "Technical rider vs hospitality rider",
        "paragraphs": [
          "A technical rider covers everything the performance needs: sound, backline, staging, and power. A hospitality rider covers the human side: dressing rooms, food and drink, transport, and accommodation. Smaller acts often fold both into a single PDF, while larger tours keep them separate.",
          "As an organizer you care about both. Catering and ground transport feed your logistics plan just as directly as backline feeds your equipment order, so it pays to read the whole document rather than stopping at the input list."
        ]
      },
      {
        "h2": "DJ vs band vs live-electronic technical riders",
        "paragraphs": [
          "Riders are not all the same shape, and knowing the genre before you open the file tells you what to expect.",
          "DJ riders are the shortest and most standardized. They center on a booth: media players (often Pioneer CDJs), a mixer, booth monitors, table dimensions, and booth height. The main variables are the exact model numbers and how many players are needed.",
          "Band riders are the longest and most variable. They carry a full input list, backline for each musician, layered monitoring, and a stage plot with many positions. Two bands of the same size can produce riders that look nothing alike.",
          "Live-electronic riders sit in between. They describe a table or riser holding synths, drum machines, laptops, controllers, and audio interfaces, sometimes hybridized with a DJ booth. The input list is shorter than a band's, but the gear list is specific and idiosyncratic, so exact models matter."
        ]
      },
      {
        "h2": "How to read a technical rider",
        "paragraphs": [
          "Reading a technical rider well is less about reading top to bottom and more about cross-checking the parts against each other. A reliable order works like this:"
        ],
        "bullets": [
          "Start with the stage plot to picture the setup before you get lost in tables.",
          "Cross-check the input list against the stage plot: every source on the plot should have a channel, and every channel should map to something on stage.",
          "Separate what the artist brings from what you must supply. Most riders mark gear as artist-provided or promoter-provided, and that line determines your cost.",
          "Note the exact models requested and whether equivalents are allowed. A CDJ-3000 request you cannot meet is a phone call in advance, not a surprise at load-in.",
          "Flag anything unusual: heavy power draw, oversized risers, specific IEM frequencies, guest positions.",
          "Pull the hospitality and transport details straight into your logistics list so nothing gets stranded in the PDF."
        ]
      },
      {
        "h2": "Why reading 30 to 40 riders by hand is the real bottleneck",
        "paragraphs": [
          "For a single show, reading one rider is a coffee's worth of work. For a festival with 30 to 40 acts across several stages, it becomes a spreadsheet marathon. Every rider is formatted differently, some arrive as scanned images, some are years out of date, and the same piece of gear shows up under three different names.",
          "At that point you are not just reading, you are reconciling. You have to total the backline across every act to place one equipment order, work out what stays on stage between sets and what has to be swapped, and calculate crew for each changeover. A single slip, one missed DI or one double-counted mixer, tends to surface at load-in when it is expensive and slow to fix. This manual reconciliation, not the reading itself, is where festival production teams lose days."
        ]
      },
      {
        "h2": "Turning technical riders into an equipment and changeover plan",
        "paragraphs": [
          "The work that actually matters is downstream of the PDF: the festival-wide equipment total, the changeover plan between sets, the crew count, and the conflicts you want to catch early. Doing that by hand means transcribing every rider into a spreadsheet first, which is exactly the step that eats the time.",
          "TRACE reads the technical riders you already receive, whether DJ, band, or live-electronic, and extracts the structured data (backline, input lists, monitoring, hospitality) with a confidence score on every field so you can see at a glance what is worth double-checking. From there it totals equipment across the whole festival, plans stage changeovers, estimates crew, flags conflicts, and pulls hospitality and transport into a logistics view. It sits between the rider-makers on the artist side and the broad ticketing platforms: it reads the riders and turns them into plans. TRACE is open source under AGPL-3.0, with a free tier so you can try it on your own riders before committing."
        ]
      }
    ],
    "faq": [
      {
        "q": "What is a technical rider in simple terms?",
        "a": "It is the \"what I need on stage\" document an artist sends before a show, covering instruments, backline, microphones, monitoring, power, and often hospitality. Production teams read it to order equipment and plan the stage."
      },
      {
        "q": "What is the difference between a technical rider and a hospitality rider?",
        "a": "The technical rider covers the performance itself: sound, backline, staging, and power. The hospitality rider covers dressing rooms, catering, transport, and accommodation. Small acts often combine them, while large tours keep them separate."
      },
      {
        "q": "What is included in a DJ technical rider?",
        "a": "Usually a booth setup: media players (often Pioneer CDJs), a mixer, booth monitors, booth height and table dimensions, and power. DJ riders are shorter and more standardized than band riders, so the main variables are the model numbers and how many players are needed."
      },
      {
        "q": "How do you read a technical rider as an organizer?",
        "a": "Start with the stage plot, cross-check it against the input list, then separate what the artist brings from what you must supply. Note the exact models and whether equivalents are allowed, and pull hospitality and transport into your logistics list."
      },
      {
        "q": "Who provides the equipment listed on a technical rider?",
        "a": "The rider should say. Most items are marked either artist-provided or promoter-provided. The promoter-provided gear is what you order and budget for, so confirming that split is the first thing to do."
      },
      {
        "q": "How do festivals handle dozens of technical riders at once?",
        "a": "They total the backline across every act into one equipment order, plan what stays or gets swapped between sets, and calculate crew per changeover. Many teams do this in spreadsheets; tools like TRACE read the riders and build the totals, changeover plan, and conflict checks for you."
      }
    ],
    "related": [
      {
        "label": "Festival production glossary",
        "href": "/glossary"
      },
      {
        "label": "For festival organizers",
        "href": "/for/festival-organizers"
      },
      {
        "label": "For production companies",
        "href": "/for/production-companies"
      },
      {
        "label": "For tour managers",
        "href": "/for/tour-managers"
      }
    ],
    "alternates": [
      {
        "hreflang": "en",
        "href": "/technical-rider"
      },
      {
        "hreflang": "it",
        "href": "/it/rider-tecnico"
      },
      {
        "hreflang": "x-default",
        "href": "/technical-rider"
      }
    ]
  },
  {
    "path": "/glossary",
    "lang": "en",
    "title": "Festival Production Glossary — Rider Terms Explained",
    "metaDescription": "A festival production glossary defining technical rider terms: stage plot, input list, backline, changeover, patch list, DI box, monitor wedge and more.",
    "ogTitle": "Festival Production Glossary: Technical Rider Terms",
    "ogDescription": "Clear definitions of the terms organizers and production crews meet in DJ, band, and live electronic technical riders — from stage plot to spare.",
    "breadcrumb": "Glossary",
    "h1": "Festival production glossary",
    "intro": "This festival production glossary defines the terms you meet in DJ, band, and live electronic technical riders — from stage plot to changeover — in plain language for organizers and production crews. Each entry is short, standalone, and written the way the work actually happens on site.",
    "sections": [
      {
        "h2": "Technical rider",
        "paragraphs": [
          "A technical rider is the document an artist or their production sends ahead of a show to specify what they need on stage: backline, audio inputs, monitors, power, and stage layout. For an organizer, it is the source of truth for what a booking will require and cost. Riders arrive as PDFs in every format imaginable; TRACE reads them and extracts the contents into structured, planning-ready data."
        ]
      },
      {
        "h2": "Stage plot",
        "paragraphs": [
          "A stage plot is a top-down drawing showing where each performer, instrument, amplifier, and monitor sits on stage. It tells the crew where to place backline and point wedges before doors open. It usually travels alongside the input list inside a technical rider. Organizers read the stage plot to check that an act fits the stage and to brief the local crew on setup."
        ]
      },
      {
        "h2": "Input list (channel list)",
        "paragraphs": [
          "An input list, also called a channel list, numbers every audio source going into the mixing console — each microphone, DI, and instrument line — in the order it should be patched. It tells the audio crew how many channels a show needs and what sits on each one. It is a core page of most technical riders, usually paired with the stage plot so placement and signal read together."
        ]
      },
      {
        "h2": "Backline",
        "paragraphs": [
          "Backline is the shared musical equipment on stage: drum kits, guitar and bass amplifiers, keyboards, and DJ gear. Riders list the backline an artist expects the festival to provide, versus what they carry themselves. For an organizer, backline is where rider requests turn into rental orders and shared-gear planning across acts on the same stage — the point where separate riders start to add up into totals."
        ]
      },
      {
        "h2": "Changeover",
        "paragraphs": [
          "A changeover is the turnaround between two acts: clearing one artist's backline and setting the next while the audience waits. Tight changeovers keep a festival on schedule; long ones eat into the running order. Organizers plan changeovers from what each rider needs on and off stage, and TRACE uses that to estimate how long each turnaround will take and where the day is at risk of slipping."
        ]
      },
      {
        "h2": "Running order",
        "paragraphs": [
          "The running order is the timed sequence of who plays when on each stage, including set lengths and the changeovers between them. It is the backbone of festival scheduling and drives crew shifts, curfews, and stage conflicts. Organizers build it from booking times and rider needs, then often share a public version so audiences know when each act is on and where."
        ]
      },
      {
        "h2": "Patch list",
        "paragraphs": [
          "A patch list maps each input to a physical connection point on stage — which stage-box input, sub-snake, or console channel a signal lands on. It is the audio crew's wiring plan, one level more detailed than the input list. It appears in more thorough technical riders and is what the crew follows during setup and line check to get every source to the right place on the desk."
        ]
      },
      {
        "h2": "DI box",
        "paragraphs": [
          "A DI box (direct injection box) converts a high-impedance instrument signal, such as a keyboard or acoustic guitar, into a balanced line the mixing console can take cleanly over a long cable run. Riders count DI boxes in the input list. Knowing how many a lineup needs across a day helps organizers and production companies stock the right audio inventory on each stage."
        ]
      },
      {
        "h2": "Monitor wedge",
        "paragraphs": [
          "A monitor wedge is a floor speaker angled back at a performer so they can hear themselves and the rest of the band on stage. Riders specify how many wedges each position needs and how many separate monitor mixes. For organizers, wedge and mix counts drive the monitor console and speaker requirements, and they shape how much work the monitor engineer carries during a set."
        ]
      },
      {
        "h2": "Load-in / load-out",
        "paragraphs": [
          "Load-in is moving an artist's gear from the truck to the stage before a show; load-out is clearing it afterward. Riders and their logistics pages note timings, access, and the crew needed for both. Organizers schedule load-in and load-out around stage changeovers and shared loading docks so that trucks, gear, and crews don't collide during the busiest windows of the day."
        ]
      },
      {
        "h2": "Advancing (a show)",
        "paragraphs": [
          "Advancing is the pre-show process of confirming every detail with each act's tour manager: rider needs, timings, backline, hospitality, and logistics. It is where an organizer turns a stack of riders into a settled plan and catches gaps before the artist arrives. Reading riders early makes advancing faster, since the open questions are already on the table by the time the call happens."
        ]
      },
      {
        "h2": "Spare / backup",
        "paragraphs": [
          "A spare, or backup, is duplicate equipment held ready in case a primary unit fails during a show — a spare CDJ, mixer, or microphone. Riders sometimes request specific spares, and festivals also keep their own by policy. For organizers, spare planning means deciding how much backup gear to carry per stage, weighed against the risk of a unit going dead in the middle of a set."
        ]
      }
    ],
    "faq": [
      {
        "q": "What's the difference between a stage plot and an input list?",
        "a": "A stage plot shows where things go — the physical placement of performers, backline, and monitors on stage. An input list shows what feeds the console — every microphone, DI, and line in patch order. The stage plot is spatial; the input list is a signal count. Most technical riders include both, and the audio crew reads them together."
      },
      {
        "q": "Where do these terms appear in a technical rider?",
        "a": "Most technical riders open with backline and stage requirements, then a stage plot and input list, and often a patch list for larger productions. Logistics pages cover load-in and load-out, and hospitality usually sits at the back. TRACE reads all of these from the PDF and turns them into structured data you can plan from."
      },
      {
        "q": "Are these terms the same for DJs, bands, and live electronic acts?",
        "a": "The core vocabulary carries across, but the detail shifts. A DJ rider centres on booth backline — CDJs, mixer, monitors — with a short input list. A band rider leans on the stage plot, a longer input list, and monitor mixes. Live electronic acts sit in between. TRACE handles all three rider types."
      }
    ],
    "related": [
      {
        "label": "What is a technical rider?",
        "href": "/technical-rider"
      },
      {
        "label": "For festival organizers",
        "href": "/for/festival-organizers"
      },
      {
        "label": "For production companies",
        "href": "/for/production-companies"
      },
      {
        "label": "For tour managers",
        "href": "/for/tour-managers"
      }
    ]
  },
  {
    "path": "/for/festival-organizers",
    "lang": "en",
    "title": "Festival Production Software for Organizers | TRACE",
    "metaDescription": "Festival production software that turns the technical riders you already receive into equipment totals, changeover plans, crew counts and conflict checks.",
    "ogTitle": "Festival production software for organizers — TRACE",
    "ogDescription": "Turn the technical riders you already receive into festival-wide equipment totals, changeover plans, crew counts, conflict checks, and a public timetable.",
    "breadcrumb": "For festival organizers",
    "h1": "Festival production software for organizers",
    "intro": "Festival production software should meet you where the work actually begins — with a confirmed lineup, a folder of technical riders in a dozen different formats, and a site plan you need to turn into stages, equipment, and crew. TRACE reads the riders you already receive and builds the plan outward from them, so the document each artist sends becomes the source for your rental order, your changeovers, and your showday.",
    "sections": [
      {
        "h2": "The job: a lineup, a pile of riders, and one showday",
        "paragraphs": [
          "As an organizer you are the point where everything converges. The booking team hands you a lineup. Each artist — a DJ, a band, a live electronic act — sends a technical rider describing what they need on stage. Those documents arrive as PDFs, scans, and exports, written to no shared standard, and every one lists gear, inputs, monitoring, and hospitality in its own way.",
          "From that pile you have to produce concrete things, usually in spreadsheets and under deadline. Broad festival management software tends to handle tickets, vendors, and budgets; TRACE handles the technical production layer — the riders, the gear, and the stages. It sits between the tools artists use to write riders and the platforms that sell tickets, and it reads what you already receive rather than asking anyone to re-enter it."
        ],
        "bullets": [
          "How many CDJs, mixers, and backline pieces to rent across the whole festival",
          "What equipment sits on each stage, on each day",
          "How one act clears the stage for the next inside its slot",
          "How much crew each stage and day needs",
          "Where two artists have asked for the same piece of gear at the same time"
        ]
      },
      {
        "h2": "Rider extraction: structured data from every technical rider",
        "paragraphs": [
          "TRACE reads each technical rider PDF and pulls out structured equipment data — the DJ booth, the backline, inputs and monitoring, power, and hospitality — mapped to schemas for DJ, band, and live-electronic setups. Text, tables, and scanned pages all go through the same pipeline, with OCR as a fallback when a rider is an image rather than selectable text.",
          "Every extracted field carries a confidence score, so you can see at a glance where the model was certain and where a page was ambiguous. Low-confidence fields are flagged for a human to check rather than passed off as fact. You review, correct, and confirm — the rider becomes clean data you can plan against, not a document you re-read five times."
        ]
      },
      {
        "h2": "Festival-wide equipment totals and a single rental order",
        "paragraphs": [
          "Once riders are structured, TRACE adds them up across every stage and every day. Instead of counting CDJs by hand, you get festival-wide equipment totals: how many of each machine the whole event needs, what each stage carries, and how demand moves from day to day. Spares are handled per stage, and the totals reflect the heaviest day, so your rental order covers the peak rather than an average.",
          "That gives you one procurement view to send to a rental company, and a stock-by-stage breakdown to hand to whoever is loading each stage."
        ]
      },
      {
        "h2": "Changeover planning and showday conflict detection",
        "paragraphs": [
          "Between two sets, a stage has to change over — one artist's gear comes down, the next goes up, inside a fixed slot. TRACE plans changeovers from the structured riders, using the canonical equipment each act actually needs, so you can see which pieces stay, which swap, and how tight each transition is.",
          "Conflict detection runs across the schedule: two artists asking for the same shared item at overlapping times, a slot that leaves no room to reset the stage, gear that appears on two stages at once. Catching these while you plan is far cheaper than discovering them at 22:00 on the night."
        ]
      },
      {
        "h2": "Crew calculation, hospitality, and transport logistics",
        "paragraphs": [
          "From the same data, TRACE estimates the crew each stage and day needs, so staffing is grounded in the actual setup rather than a guess. Hospitality riders are extracted alongside the technical ones, so catering and dressing-room requests are collected in one place instead of scattered across inboxes.",
          "For the parts that happen off-stage, the logistics module covers hotels, airports, and ground transport, including a transport Gantt to lay out pickups and drop-offs across the festival. It reads the same artist roster, so travel and production stay in step."
        ]
      },
      {
        "h2": "A free public festival timetable for your audience",
        "paragraphs": [
          "When the schedule is set, TRACE publishes a public festival timetable your audience can open — stages, set times, and lineup — without another tool or a separate ticket platform. It is free to publish, and it stays in sync with the running order you already maintain inside TRACE, so the version the public sees matches the one your crew works from.",
          "TRACE is open source under AGPL-3.0, with a Free tier and paid plans that scale with the size of your operation. You can read the core, run it, and start with a single festival before you commit to more."
        ]
      }
    ],
    "faq": [
      {
        "q": "Is TRACE a rider maker or a festival platform?",
        "a": "Neither. TRACE reads the technical riders you already receive from artists and turns them into equipment totals, changeovers, crew, and logistics. It sits between the tools artists use to write riders and the broad platforms that handle tickets and vendors — it does the production planning in the middle."
      },
      {
        "q": "What kinds of riders can it read?",
        "a": "DJ, band, and live-electronic riders delivered as PDFs, including scanned documents, which run through OCR. Each act is mapped to a schema that fits its setup, and every extracted field comes with a confidence score you can review."
      },
      {
        "q": "Do I have to trust the AI blindly?",
        "a": "No. Extraction is a starting point, not the final word. Low-confidence fields are flagged for human review, and you confirm or correct everything before it feeds the plan. The workflow is built around a person checking the machine, not being replaced by it."
      },
      {
        "q": "How much does festival production software like TRACE cost?",
        "a": "TRACE has a Free tier with 10 riders a month, then Explorer at €49, Builder at €149, and Scale at €399, with an Enterprise option for larger operations. It is open source under AGPL-3.0, so the core is always available to read and run."
      },
      {
        "q": "Can the audience see the schedule?",
        "a": "Yes. TRACE publishes a free public festival timetable with stages and set times that stays in sync with your internal running order, so you never maintain two separate schedules."
      }
    ],
    "related": [
      {
        "label": "What is a technical rider?",
        "href": "/technical-rider"
      },
      {
        "label": "For production companies",
        "href": "/for/production-companies"
      },
      {
        "label": "For tour managers",
        "href": "/for/tour-managers"
      },
      {
        "label": "Festival production glossary",
        "href": "/glossary"
      }
    ]
  },
  {
    "path": "/for/production-companies",
    "lang": "en",
    "title": "Festival Equipment Management for Production Teams",
    "metaDescription": "Turn artist riders into festival-wide equipment totals, per-stage stock, pull sheets, spares and changeover plans your production team can trust.",
    "ogTitle": "Festival Equipment Management for Production Companies | TRACE",
    "ogDescription": "TRACE reads the technical riders you already receive and turns them into festival-wide equipment totals, per-stage pull sheets, spare counts and a rental order backed by the source.",
    "breadcrumb": "For production companies",
    "h1": "Festival equipment management for production companies",
    "intro": "Festival equipment management starts long before load-in — it starts with a stack of technical riders, one per act, each written in its own format. TRACE reads those rider PDFs and turns them into festival-wide equipment totals, per-stage stock, pull sheets and a rental order you can defend, so your production team plans from structured data instead of a spreadsheet you retyped by hand.",
    "sections": [
      {
        "h2": "From artist riders to festival-wide equipment totals",
        "paragraphs": [
          "Every act sends a technical rider in its own layout — a DJ rider listing CDJs and a mixer, a band's input list and backline, a live-electronic setup with its own patch. TRACE parses each PDF and extracts the equipment as structured data, with a confidence score on every field so you can see at a glance which numbers to trust and which to check before they matter.",
          "From there it aggregates. How many CDJ-3000s, mixers, wedges, DI boxes and microphones the whole festival needs, rolled up by act, by stage and by day — the tally you used to build by hand across forty riders, kept in one model that updates when a rider changes."
        ],
        "bullets": [
          "Extraction across DJ, band and live-electronic riders into one equipment model",
          "A confidence score on every extracted field, with low-confidence values flagged for review",
          "Totals rolled up by act, stage and festival day"
        ]
      },
      {
        "h2": "Per-stage stock and pull sheets for the warehouse",
        "paragraphs": [
          "A festival total is only half the picture — the warehouse loads by stage. TRACE holds per-stage stock, so you can see what lives on each stage for the day and what floats between them. When the plan is settled, export a pull sheet your crew can pick against, plus equipment lists and PDF reports for the production book."
        ],
        "bullets": [
          "Per-stage stock, separating gear that stays put from gear that floats",
          "Pull sheets and equipment lists you can hand straight to the warehouse",
          "PDF reports for the file and the production book"
        ]
      },
      {
        "h2": "Changeover deltas between acts",
        "paragraphs": [
          "Between two acts on the same stage, the question is never the whole setup — it's the delta. TRACE compares consecutive acts and shows exactly what changes at each changeover: what to add, what to strike, what to reposition inside the window. Your stage crew works from a short, specific list instead of re-reading two full riders under time pressure, and you can see where a changeover is tight before the schedule is locked."
        ]
      },
      {
        "h2": "Spare calculation and a rental order you can trust",
        "paragraphs": [
          "The number that has to be right is the one you rent against. TRACE calculates spares with a default policy — for example a spare for every few CDJs or turntables, and one per mixer — that you can override per stage where the risk or the budget is different. Subtract what you own from the festival total and what's left is a sub-hire order backed by the riders it came from, with any low-confidence field marked so nothing goes out unverified."
        ],
        "bullets": [
          "Automatic spare calculation with per-stage overrides",
          "Owned stock subtracted from festival totals to size the rental and sub-hire order",
          "Every quantity traceable back to the rider and its confidence score"
        ]
      },
      {
        "h2": "Where TRACE fits in your AV production planning",
        "paragraphs": [
          "TRACE sits between the tools that make riders and the platforms that sell tickets and manage vendors. It doesn't replace either — it reads the riders you already receive and turns them into an AV production plan: equipment totals, backline management, crew figures, changeovers and conflict checks, with hospitality and logistics when you need them.",
          "It's open source under AGPL-3.0, with a free tier and paid plans — Explorer, Builder, Scale and Enterprise — that grow with your festival roster rather than your headcount."
        ]
      }
    ],
    "faq": [
      {
        "q": "How does TRACE build festival-wide equipment totals from riders?",
        "a": "It reads each technical rider PDF, extracts the equipment as structured data with a confidence score on every field, then aggregates across all acts, stages and days into a single set of totals. When a rider changes, the totals update with it."
      },
      {
        "q": "Can I trust the numbers enough to place a rental order?",
        "a": "Every extracted field carries a confidence score, and low-confidence values are flagged for review. You approve the figures before they become a pull sheet or a sub-hire order, and each quantity stays traceable back to the rider it came from — so the order is defensible, not a black box."
      },
      {
        "q": "How does spare calculation work?",
        "a": "TRACE applies a default spare policy — for instance a spare for every few CDJs or turntables and one per mixer — and lets you override it per stage where the risk or budget differs. Spares are included in the festival total that sizes your rental order."
      },
      {
        "q": "Does it handle changeovers between acts?",
        "a": "Yes. For consecutive acts on the same stage, TRACE computes the delta — what to add, strike or reposition during the changeover window — so your crew works from a short, specific list instead of two full riders."
      },
      {
        "q": "What can I export for the crew and the warehouse?",
        "a": "Pull sheets and equipment lists for picking, per-stage stock views, changeover lists for stage crew, and PDF reports for the production book. The extracted, aggregated data feeds all of them."
      }
    ],
    "related": [
      {
        "label": "What is a technical rider?",
        "href": "/technical-rider"
      },
      {
        "label": "Festival production glossary",
        "href": "/glossary"
      },
      {
        "label": "For festival organizers",
        "href": "/for/festival-organizers"
      },
      {
        "label": "For tour managers",
        "href": "/for/tour-managers"
      }
    ]
  },
  {
    "path": "/for/tour-managers",
    "lang": "en",
    "title": "Tour Production Management Software | TRACE",
    "metaDescription": "Advance festival dates with confidence. TRACE reads your artist's technical rider, reflects it in the festival plan, and flags conflicts before showday.",
    "ogTitle": "Tour production management, from advance to showday",
    "ogDescription": "TRACE reads your artist's technical rider and carries it into the festival's equipment, changeover, hospitality, and logistics plans.",
    "breadcrumb": "For tour managers",
    "h1": "Tour production management, from advance to showday",
    "intro": "Tour production management lives in the gap between the technical rider you send and the plan a festival actually builds from it. TRACE closes that gap: it reads your artist's rider PDF, turns every requirement into structured, confidence-scored data, and carries it straight into the festival's equipment, changeover, hospitality, and logistics plans — so what you advanced is what shows up on the day.",
    "sections": [
      {
        "h2": "Advance festival dates without the email archaeology",
        "paragraphs": [
          "Advancing a festival date usually means chasing the latest rider version through a long email thread, then re-typing its contents into someone else's spreadsheet. TRACE takes the rider PDF you send and reads it into structured data — DJ, band, or live electronic — so the production you are advancing with works from the document itself, not a half-remembered summary of it.",
          "Every extracted field carries a confidence score, so the requirements that matter most — CDJ count, mixer model, monitor mixes, power — trace back to the exact line in your rider that produced them. When you send an updated version, it gets read again, and the plan follows the rider instead of drifting away from it."
        ]
      },
      {
        "h2": "Make sure your artist's technical rider is actually read",
        "paragraphs": [
          "The failure mode when advancing is rarely disagreement — it is omission. A DI line missed, the wrong number of turntables, a spare that never got counted. TRACE reads the whole technical rider, including body text, equipment tables, and scanned or photographed pages through OCR, and maps it to structured equipment schemas rather than skimming for keywords.",
          "If the festival invites you into the artist portal, you can see exactly what was extracted from your rider, check the confidence score on each field, correct anything misread, and upload updated documents. Your side reviews and confirms while the festival keeps control of its own plan. What you sign off is what feeds the day."
        ],
        "bullets": [
          "Backline and DJ equipment — CDJs, mixers, turntables — with model and quantity",
          "Input lists, DI, and monitor requirements for bands and live-electronic acts",
          "Hospitality and rider notes, kept alongside the technical detail"
        ]
      },
      {
        "h2": "Spot equipment and changeover conflicts before showday",
        "paragraphs": [
          "Once every rider on a stage is structured, TRACE plans across all of them at once: festival-wide equipment totals, per-stage changeover timing, crew load, and conflict detection. Your artist's specific needs sit against the shared stage, so a clash — two acts wanting the same backline in back-to-back slots, a changeover too tight to strike and reset, a missing spare — surfaces days ahead instead of at load-in.",
          "For a tour manager, that early warning is the whole point. You learn that a stage cannot turn your set around in the eight minutes between acts while there is still time to move a slot or add a piece of kit — not when the crew is already on the clock."
        ]
      },
      {
        "h2": "Keep hospitality and logistics straight across the run",
        "paragraphs": [
          "A rider is more than a channel list. TRACE also extracts hospitality requirements and feeds the logistics side of a festival — a transport Gantt, hotels, and airports — so ground moves, room needs, and arrival windows live in the same plan as the technical detail.",
          "Across a run of dates, the same rider drives each stop. Set times land on the free public festival timetable that your artist and crew can check without an account, and the production detail stays where the people building the show can act on it."
        ]
      },
      {
        "h2": "Where TRACE fits for tour and artist managers",
        "paragraphs": [
          "TRACE is not a rider builder and not a ticketing platform. It reads the technical riders you already send and turns them into festival production plans. In practice, the festivals and production companies you tour with run TRACE, and you are brought in through the artist portal to review and confirm — though you can also run your own account to advance a run of dates yourself.",
          "It is open source under AGPL-3.0, with a free tier to start and paid tiers (Explorer, Builder, Scale, Enterprise) as your calendar grows."
        ]
      }
    ],
    "faq": [
      {
        "q": "Does TRACE replace the technical rider I send to festivals?",
        "a": "No. TRACE reads the rider you already send — it does not build or replace it. Your PDF stays the source of truth; TRACE turns it into structured, confidence-scored data the festival can plan against."
      },
      {
        "q": "Do tour managers need their own TRACE account?",
        "a": "Often not. The festival or production company runs TRACE and invites you into the artist portal to review what was extracted from your rider. If you advance dates yourself, you can run your own account — there is a free tier to start."
      },
      {
        "q": "Can I confirm what a festival extracted from my rider before showday?",
        "a": "Yes. Through the artist portal you can review the structured data, see the confidence score on every field, correct anything misread, and upload updated documents. Your side confirms; the festival keeps control of its own plan."
      },
      {
        "q": "How does TRACE handle a run of multiple festival dates?",
        "a": "Each date is its own festival plan, but the same rider feeds each one. Update the rider once and re-import it where it is needed — equipment totals, changeovers, hospitality, and logistics recompute per event."
      },
      {
        "q": "Is TRACE free to try?",
        "a": "Yes. There is a Free tier with 10 riders a month, then Explorer (€49), Builder (€149), Scale (€399), and Enterprise. TRACE is open source under the AGPL-3.0 license."
      }
    ],
    "related": [
      {
        "label": "What is a technical rider?",
        "href": "/technical-rider"
      },
      {
        "label": "For festival organizers",
        "href": "/for/festival-organizers"
      },
      {
        "label": "For production companies",
        "href": "/for/production-companies"
      },
      {
        "label": "Festival production glossary",
        "href": "/glossary"
      }
    ]
  },
  {
    "path": "/it/rider-tecnico",
    "lang": "it",
    "title": "Rider tecnico: cos'è e come si legge",
    "metaDescription": "Il rider tecnico è la specifica di palco dell'artista: backline, input list, monitor. Guida per organizzatori: cos'è, come si legge e diventa un piano.",
    "ogTitle": "Rider tecnico: cos'è e come si legge",
    "ogDescription": "Cos'è un rider tecnico, come si legge e come diventa un piano di produzione per un festival: totali attrezzatura, cambi palco, crew e logistica.",
    "breadcrumb": "Rider tecnico",
    "h1": "Rider tecnico: cos'è e come si legge",
    "intro": "Il rider tecnico è il documento con cui un artista dice alla produzione di cosa ha bisogno sul palco: strumenti, impianto, microfoni, monitor e collegamenti. Per chi organizza un festival, saperlo leggere in fretta e senza ambiguità è la differenza tra un cambio palco pulito e mezz'ora persa a cercare un cavo. Questa guida spiega cos'è, come si legge e come si trasforma in un piano di produzione.",
    "sections": [
      {
        "h2": "Rider tecnico: cos'è e a cosa serve",
        "paragraphs": [
          "Un rider tecnico (technical rider) è la specifica che un artista allega al contratto per dire alla produzione di cosa ha bisogno sul palco. Elenca la backline — gli strumenti e le macchine che l'artista porta o richiede — insieme a impianto audio, monitor, alimentazione e collegamenti. In molti casi include anche indicazioni su luci e video. È un documento tecnico, non commerciale: parla di segnale, non di cachet.",
          "Va tenuto distinto dall'hospitality rider, che riguarda camerini, catering, alloggio e trasferimenti. Il rider tecnico serve a una cosa sola: mettere la produzione in condizione di far suonare l'artista come previsto, senza improvvisare la sera stessa. Chi lo scrive è l'artista o il tour manager; chi lo riceve, lo legge e lo rende realizzabile è chi organizza l'evento o la società tecnica."
        ]
      },
      {
        "h2": "Cosa contiene un rider tecnico",
        "paragraphs": [
          "Il contenuto varia da artista ad artista, ma le voci ricorrenti sono sempre le stesse. Conoscerle aiuta a capire in pochi secondi se un rider è completo o se manca qualcosa."
        ],
        "bullets": [
          "Input list — l'elenco dei segnali che entrano nel mixer, canale per canale (voce, cassa, basso, tastiere). Per il fonico è la parte più importante del documento.",
          "Stage plot — la piantina del palco: dove stanno musicisti, amplificatori, monitor e prese. Dice al service dove posizionare ogni cosa.",
          "Backline — strumenti e macchine richiesti: batteria, amplificatori e, per i DJ, i lettori (spesso Pioneer CDJ-3000) e il mixer.",
          "Monitoraggio — quante spie (monitor da palco) o sistemi in-ear servono, e come vanno mandati i mix.",
          "Alimentazione e collegamenti — prese, tensioni, DI box (le scatole che adattano un segnale strumentale all'ingresso del mixer) e lunghezze cavi.",
          "Note operative — a volte orari di soundcheck, crew richiesta e tempi di montaggio."
        ]
      },
      {
        "h2": "Come si legge un rider tecnico",
        "paragraphs": [
          "Leggere un rider tecnico significa tradurre il documento dell'artista in ciò che il tuo palco può davvero offrire. Il percorso è quasi sempre lo stesso."
        ],
        "bullets": [
          "Parti dall'input list: conta i canali e verifica di avere ingressi, microfoni e DI box a sufficienza.",
          "Confronta lo stage plot con le misure reali del palco e con la posizione di prese e regia.",
          "Controlla i modelli richiesti: un rider che chiede tre CDJ-3000 e un mixer preciso non è uguale a uno che accetta alternative equivalenti.",
          "Segna le differenze rispetto a ciò che hai già a noleggio o in casa: quello che manca diventa una richiesta al service.",
          "Verifica data e versione: un rider vecchio può elencare macchine che l'artista non usa più."
        ]
      },
      {
        "h2": "Come funziona un rider tecnico durante il festival",
        "paragraphs": [
          "Un rider tecnico non vive da solo: funziona quando i dati di decine di artisti si sommano in un unico piano di palco. Qui il singolo documento diventa numeri di festival.",
          "Da tutti i rider di un palco si ricavano i totali di attrezzatura (quanti lettori, quanti mixer, quante spie servono nel picco della giornata), i tempi di cambio palco tra un artista e l'altro, la crew necessaria e i conflitti — per esempio due headliner che chiedono lo stesso pezzo raro nello stesso momento. È il passaggio che trasforma una cartella di PDF in una lista di noleggio e in un running order realistico."
        ]
      },
      {
        "h2": "Rider tecnico per DJ, band e live electronic",
        "paragraphs": [
          "Non tutti i rider si leggono allo stesso modo. Un set DJ ruota attorno alla console: lettori, mixer, a volte un giradischi, e conta soprattutto la compatibilità dei modelli. Un rider di band è più pesante sull'input list e sul monitoraggio, con molti canali e mix diversi. Il live electronic sta nel mezzo: hardware, controller e laptop, con un patch (lo schema dei collegamenti audio) che cambia da artista ad artista.",
          "Per chi programma un festival multi-genere, questa varietà è il vero lavoro: schemi diversi che devono comunque incastrarsi sullo stesso palco, nello stesso giorno, con gli stessi cambi."
        ]
      },
      {
        "h2": "Dal rider al piano: software gestione festival",
        "paragraphs": [
          "Fare tutto questo a mano — aprire ogni PDF, ricopiare canali e modelli in un foglio, sommare a occhio — regge per pochi artisti e si sfilaccia su una line-up intera. Un software gestione festival come TRACE nasce per questo punto preciso: legge i rider tecnici che ricevi e li trasforma in dati strutturati.",
          "TRACE estrae i campi da ogni PDF con l'AI e assegna a ciascun valore un punteggio di affidabilità (confidence), così sai quali dati fidarti subito e quali rivedere. Da lì calcola i totali di attrezzatura, pianifica i cambi palco, stima la crew, segnala i conflitti e gestisce hospitality e logistica (Gantt dei trasporti, hotel, aeroporti). Pubblica anche un timetable pubblico e gratuito del festival.",
          "La differenza rispetto a una piattaforma generica per eventi è il punto di partenza. TRACE non vende biglietti né gestisce fornitori: parte dal rider tecnico, il documento che ogni produzione riceve e che nessuno ha voglia di rileggere venti volte a mano. Non crea i rider al posto dell'artista: legge quelli che hai già in mano e ti restituisce un piano."
        ]
      }
    ],
    "faq": [
      {
        "q": "Qual è la differenza tra rider tecnico e hospitality rider?",
        "a": "Il rider tecnico riguarda il palco — backline, audio, monitor, collegamenti. L'hospitality rider riguarda camerini, catering, alloggio e trasferimenti. Spesso arrivano nello stesso file, ma servono a reparti diversi della produzione."
      },
      {
        "q": "Chi prepara il rider tecnico?",
        "a": "Lo prepara l'artista o il suo tour manager, a volte insieme al fonico di fiducia. Chi organizza il festival lo riceve, lo legge e lo rende realizzabile sul proprio palco. TRACE sta dal lato di chi riceve: legge i rider, non li scrive."
      },
      {
        "q": "Cos'è l'input list in un rider tecnico?",
        "a": "È l'elenco dei segnali che entrano nel mixer, canale per canale: voce, cassa, basso, tastiere e così via. Per il fonico è la parte più importante, perché dice quanti ingressi, microfoni e DI box servono."
      },
      {
        "q": "Cos'è lo stage plot?",
        "a": "Lo stage plot è la piantina del palco: mostra dove stanno musicisti, amplificatori, monitor e prese. Serve al service per posizionare l'attrezzatura e per capire se tutto entra nello spazio disponibile."
      },
      {
        "q": "Ogni quanto va aggiornato un rider tecnico?",
        "a": "Idealmente a ogni cambio di setup o di stagione. Un rider vecchio può elencare macchine che l'artista non usa più — per esempio un modello di lettore superato — e portare la produzione a preparare l'attrezzatura sbagliata. Controllare sempre data e versione."
      },
      {
        "q": "TRACE crea i rider tecnici o li legge?",
        "a": "Li legge. TRACE parte dai PDF che ricevi dagli artisti, ne estrae i dati con un punteggio di affidabilità su ogni campo, e li trasforma in totali di attrezzatura, cambi palco, crew e logistica. La stesura del rider resta all'artista."
      }
    ],
    "related": [
      {
        "label": "What is a technical rider? (EN)",
        "href": "/technical-rider"
      },
      {
        "label": "Glossario della produzione festival",
        "href": "/glossary"
      },
      {
        "label": "Per gli organizzatori di festival",
        "href": "/for/festival-organizers"
      },
      {
        "label": "TRACE",
        "href": "/"
      }
    ],
    "alternates": [
      {
        "hreflang": "en",
        "href": "/technical-rider"
      },
      {
        "hreflang": "it",
        "href": "/it/rider-tecnico"
      },
      {
        "hreflang": "x-default",
        "href": "/technical-rider"
      }
    ]
  }
];

export function seoPageByPath(p: string): SeoPageData | undefined {
  return SEO_PAGES.find((x) => x.path === p);
}
